import React, { useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback } from "react";
import { getGeminiKey } from "./geminiAI";
import { readAllData } from "./hayah/aiContext";
import { createPortal } from "react-dom";
import { CapacitorHealthkit, SampleNames } from "@perfood/capacitor-healthkit";
import { registerPlugin, Capacitor } from '@capacitor/core';
import { hapticTap, hapticHayahSuccess } from '../hayahHaptics';
import { loadReadinessCache } from "./hayah/readinessCache";
import { generateReadiness } from "./hayah/readinessAI";
import { getMounjaroDay } from "./hayah/mounjaroCycle";
import { getTodayReading as getTodayKetone, addKetoneReading } from "./hayah/ketoneLog";
import { analyseKetonePhoto } from "./hayah/ketoneCapture";
import {
  useHayahPrefs,
  computeFastingWindow,
  computeCurrentMealWindow,
  FASTING_STAGES,
  fmtClock as fmtClockPref,
  fmtHM,
  loadFastingOverrides,
  patchFastingOverride,
  FASTING_MILESTONES,
  nextMilestone,
  maybeFireFastingMilestones,
  maybeFireStageMilestones,
} from "./hayahPrefs";

const GhidhaHealth    = registerPlugin('GhidhaHealth');
const CapacitorCamera = registerPlugin('Camera');

// True when a text input / textarea / select currently has focus. Used to
// suppress global parent re-renders (storage / focus / visibilitychange /
// tick intervals) while the user is typing — those re-renders cause iOS
// WKWebView to invalidate the active keyboard focus and dismiss it.
function isUserEditing() {
  if (typeof document === "undefined") return false;
  const a = document.activeElement;
  if (!a) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !a.disabled;
}

// Same defaults CalorieCounter uses, mirrored here so the two views agree
// when no goals have been set yet. Keep in sync with DEFAULT_GOALS in
// src/components/CalorieCounter.js.
const HAYAH_DEFAULT_GOALS = {
  calories: 2000, protein: 150, carbs: 225, fat: 65,
  steps: 12000, burn: 3000, sleep: 8,
};

export function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readNum(v, fallback = 0) {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

// Reads Apple Health data and merges it into calorieLog for today.
// Mirrors the essential fields from CalorieCounter's readAppleHealth.
async function pullHealthForToday(bumpTick) {
  try {
    await CapacitorHealthkit.requestAuthorization({
      all: [], write: [], read: ["calories", "steps", "activity", "sleepAnalysis"],
    });
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const now = new Date();
    const yesterday6pm = new Date(startOfDay); yesterday6pm.setHours(-6);
    const opts = { startDate: startOfDay.toISOString(), endDate: now.toISOString(), limit: 0 };
    const sleepOpts = { startDate: yesterday6pm.toISOString(), endDate: now.toISOString(), limit: 0 };

    const [activeRes, stepsRes, basalRes, sleepRes] = await Promise.all([
      CapacitorHealthkit.queryHKitSampleType({ ...opts, sampleName: SampleNames.ACTIVE_ENERGY_BURNED }),
      CapacitorHealthkit.queryHKitSampleType({ ...opts, sampleName: SampleNames.STEP_COUNT }),
      CapacitorHealthkit.queryHKitSampleType({ ...opts, sampleName: SampleNames.BASAL_ENERGY_BURNED }),
      CapacitorHealthkit.queryHKitSampleType({ ...sleepOpts, sampleName: SampleNames.SLEEP_ANALYSIS }),
    ]);

    const burned = Math.round(activeRes.resultData.reduce((s, r) => s + (r.value || 0), 0));
    const basalBySource = {};
    for (const r of basalRes.resultData) {
      const src = r.sourceBundleId || r.source || "unknown";
      basalBySource[src] = (basalBySource[src] || 0) + (r.value || 0);
    }
    const basalBurned = Math.round(Math.max(0, ...Object.values(basalBySource)));
    const stepsBySource = {};
    for (const r of stepsRes.resultData) {
      const src = r.sourceBundleId || r.source || "unknown";
      stepsBySource[src] = (stepsBySource[src] || 0) + (r.value || 0);
    }
    const steps = Math.round(Math.max(0, ...Object.values(stepsBySource)));

    let sleepMinutes = 0, deepMinutes = 0, remMinutes = 0, awakeMinutes = 0;
    const isDeep  = s => s === 4 || s === "4" || /deep/i.test(String(s));
    const isREM   = s => s === 5 || s === "5" || /rem/i.test(String(s));
    const isSleep = s => [1,3,"1","3"].includes(s) || /asleep|core|unspecified/i.test(String(s));
    try {
      const nativeSleep = await GhidhaHealth.readSleepData();
      const samples = nativeSleep?.data || [];
      const hasDetailed = samples.some(r =>
        r.sleepState === "ASLEEPCORE" || r.sleepState === "ASLEEPDEEP" || r.sleepState === "ASLEEPREM"
      );
      for (const r of samples) {
        const st = r.sleepState;
        if (hasDetailed && st === "ASLEEP") continue;
        if (st === "INBED" || st === "UNKNOWN") continue;
        const mins = Math.round((new Date(r.endDate) - new Date(r.startDate)) / 60000);
        if (mins <= 0) continue;
        if (st === "AWAKE") awakeMinutes += mins;
        else if (isDeep(st)) { deepMinutes += mins; sleepMinutes += mins; }
        else if (isREM(st))  { remMinutes  += mins; sleepMinutes += mins; }
        else if (st === "ASLEEPCORE") sleepMinutes += mins;
        else if (!hasDetailed && isSleep(st)) sleepMinutes += mins;
      }
    } catch {
      for (const r of (sleepRes.resultData || [])) {
        const st = r.sleepState;
        if (st === "INBED" || st === "UNKNOWN") continue;
        const mins = Math.round((new Date(r.endDate) - new Date(r.startDate)) / 60000);
        if (mins <= 0) continue;
        if (st === "AWAKE") awakeMinutes += mins;
        else if (isDeep(st)) { deepMinutes += mins; sleepMinutes += mins; }
        else if (isREM(st))  { remMinutes  += mins; sleepMinutes += mins; }
        else if (isSleep(st)) sleepMinutes += mins;
      }
    }

    let bodyMassKg = 0, bodyFatPct = 0;
    let bodyHistory = { weight: [], bodyFat: [] };
    try {
      const bc = await GhidhaHealth.readBodyComposition();
      bodyHistory.weight  = (bc?.weightSamples  || []).map(s => ({ date: s.date, value: s.value }));
      bodyHistory.bodyFat = (bc?.bodyFatSamples || []).map(s => ({ date: s.date, value: s.value }));
      if (bodyHistory.weight.length  > 0) bodyMassKg = parseFloat((bodyHistory.weight[bodyHistory.weight.length - 1].value   || 0).toFixed(1));
      if (bodyHistory.bodyFat.length > 0) bodyFatPct = parseFloat((bodyHistory.bodyFat[bodyHistory.bodyFat.length - 1].value || 0).toFixed(1));
    } catch {}

    const today = localDateStr();
    let log = {};
    try { log = JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch {}
    if (!log[today]) log[today] = { meals: { breakfast: [], lunch: [], dinner: [], snacks: [], supplements: [] } };
    if (!log[today].meals) log[today].meals = { breakfast: [], lunch: [], dinner: [], snacks: [], supplements: [] };
    log[today].caloriesBurned = burned;
    log[today].basalBurned    = basalBurned;
    log[today].steps          = steps;
    log[today].sleepMinutes   = sleepMinutes;
    log[today].deepMinutes    = deepMinutes;
    log[today].remMinutes     = remMinutes;
    log[today].awakeMinutes   = awakeMinutes;
    if (bodyMassKg > 0) log[today].bodyMassKg = bodyMassKg;
    if (bodyFatPct > 0) log[today].bodyFatPct = bodyFatPct;
    if (bodyHistory.weight.length || bodyHistory.bodyFat.length) log[today].bodyHistory = bodyHistory;
    localStorage.setItem("calorieLog", JSON.stringify(log));
    bumpTick();
    return { ok: true, steps, activeBurn: burned, basalBurn: basalBurned, sleepMinutes, weightKg: bodyMassKg, bodyFatPct };
  } catch {
    return { ok: false };
  }
}

// Aggregates today's entry from `calorieLog` + user's `calorieGoals`.
// Re-reads on focus / visibilitychange / storage events so the dashboard
// stays in sync after the user logs a meal in the Ghidha screen.
export function useHayahTodayData(prefs) {
  const [tick, setTick] = useState(0);
  const bumpTick = useCallback(() => setTick(t => t + 1), []);
  useEffect(() => {
    // Guard every bump path: don't trigger a parent re-render while the
    // user is actively typing in an input/textarea/select. The re-render
    // chain causes iOS WKWebView to invalidate the focus and dismiss the
    // on-screen keyboard. The deferred state is picked up the next time
    // the user blurs (focus event will fire again).
    const bump = () => { if (!isUserEditing()) setTick(t => t + 1); };
    const onStorage = e => {
      if (isUserEditing()) return;
      if (!e.key || e.key === "calorieLog" || e.key === "calorieGoals" || e.key === "fastingOverrides" || e.key === "hayahPrefs" || e.key === "hayahKetoneLog" || e.key === "hayahReadinessToday") bump();
    };
    const onVis = () => { if (document.visibilityState === "visible") bump(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Re-tick once a minute while a fast is in progress so the live counter
  // advances. Cheap: re-reads localStorage + recomputes derived metrics.
  useEffect(() => {
    let log = {}, overrides = {};
    try { log = JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch {}
    try { overrides = loadFastingOverrides(); } catch {}
    const fastingSettings = {
      tolerance: prefs.fasting.calorieTolerance,
      allowList: prefs.fasting.allowList,
      bedtimeOffsetMin: prefs.fasting.bedtimeOffsetMin,
    };
    const f = computeFastingWindow(log, localDateStr(), overrides, fastingSettings);
    const cm = computeCurrentMealWindow(log, localDateStr(), prefs);
    // Fire milestone notifications when reminders enabled
    try { maybeFireFastingMilestones(prefs, f); } catch {}
    try { maybeFireStageMilestones(prefs, cm); } catch {}
    // Keep ticking whenever any live counter is running — fast in
    // progress OR an eating-mode since-last-meal counter that hasn't
    // yet aged past 24h.
    const ticking = f.inProgress || (cm.mode === "eating" && Number.isFinite(cm.hoursSinceLastMeal) && cm.hoursSinceLastMeal < 24);
    if (!ticking) return;
    const id = setInterval(() => { if (!isUserEditing()) setTick(t => t + 1); }, 60000);
    return () => clearInterval(id);
  }, [tick, prefs]);

  const data = useMemo(() => {
    let log = {};
    let goals = HAYAH_DEFAULT_GOALS;
    try { log = JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch {}
    try {
      goals = { ...HAYAH_DEFAULT_GOALS, ...JSON.parse(localStorage.getItem("calorieGoals") || "{}") };
    } catch {}

    const today = log[localDateStr()] || {};
    const meals = today.meals || { breakfast: [], lunch: [], dinner: [] };
    // Sum across ALL meal slots, not a hardcoded subset — the schema includes
    // meal/supplements/whey/snacks (and legacy breakfast/lunch/dinner) and any
    // hardcoded list silently drops calories from missing slots.
    const allMeals = Object.values(meals).filter(Array.isArray).flat();

    let kcal = 0, p = 0, c = 0, f = 0;
    for (const m of allMeals) {
      kcal += readNum(m.calories);
      p += readNum(m.protein);
      c += readNum(m.carbs);
      f += readNum(m.fat);
    }

    const burnedActive = readNum(today.caloriesBurned);
    const burnedBasal = readNum(today.basalBurned);
    const burnedTotal = burnedActive + burnedBasal;
    const intake = Math.round(kcal);
    const intakeGoal = readNum(goals.calories, HAYAH_DEFAULT_GOALS.calories);
    // Calories Left = goal − intake. Signed: negative means the user has
    // exceeded their goal — the OrbitCluster ring then flips its label from
    // LEFT to OVER and shows the surplus. Matches CalorieCounter's
    // `remaining = goals.calories − totalCalories`.
    const remaining = intakeGoal - intake;
    const netDeficit = intake - burnedTotal;

    const sleepMins = readNum(today.sleepMinutes);
    const deep = readNum(today.deepMinutes);
    const rem = readNum(today.remMinutes);
    const awake = readNum(today.awakeMinutes);
    const light = Math.max(0, sleepMins - deep - rem);

    const todayStr = localDateStr();
    let overrides = {};
    try { overrides = loadFastingOverrides(); } catch {}
    const fastingSettings = {
      tolerance: prefs.fasting.calorieTolerance,
      allowList: prefs.fasting.allowList,
      bedtimeOffsetMin: prefs.fasting.bedtimeOffsetMin,
    };
    const fastingToday = computeFastingWindow(log, todayStr, overrides, fastingSettings);
    // Live "since last meal today" companion to the above. Phase-flip
    // logic in FastingCard chooses which one to hero.
    const currentMeal = computeCurrentMealWindow(log, todayStr, prefs);
    const fastingHistory = [];
    const HISTORY_DAYS = 30;     // longer window so overlay can show 30D avg + trend
    for (let i = 0; i < HISTORY_DAYS; i++) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const ds = localDateStr(d);
      fastingHistory.push({ date: ds, ...computeFastingWindow(log, ds, overrides, fastingSettings) });
    }
    fastingHistory.reverse();

    const todayMealEntries = Object.values(meals).filter(Array.isArray).flat();
    const todayMealTs = todayMealEntries
      .map(m => (m?.loggedAt ? Date.parse(m.loggedAt) : NaN))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    return {
      hasData: !!log[localDateStr()],
      streakDays: calcCalorieStreak(log),
      intake,
      intakeGoal: Math.round(intakeGoal),
      burned: Math.round(burnedTotal),
      activeBurn: Math.round(burnedActive),
      remaining: Math.round(remaining), // signed — negative means over goal
      netDeficit: Math.round(netDeficit),
      protein: { value: Math.round(p), max: Math.round(readNum(goals.protein, HAYAH_DEFAULT_GOALS.protein)) },
      carbs:   { value: Math.round(c), max: Math.round(readNum(goals.carbs,   HAYAH_DEFAULT_GOALS.carbs)) },
      fat:     { value: Math.round(f), max: Math.round(readNum(goals.fat,     HAYAH_DEFAULT_GOALS.fat)) },
      steps: Math.round(readNum(today.steps)),
      stepsGoal: Math.round(readNum(goals.steps, HAYAH_DEFAULT_GOALS.steps)),
      weightKg: readNum(today.bodyMassKg),
      bodyFatPct: readNum(today.bodyFatPct),
      sleep: {
        totalMins: Math.round(sleepMins + awake),
        deep: Math.round(deep),
        rem: Math.round(rem),
        light: Math.round(light),
        awake: Math.round(awake),
        startLabel: "",
        endLabel: "",
        goalHours: Math.round(readNum(goals.sleep, HAYAH_DEFAULT_GOALS.sleep)),
      },
      fasting: {
        ...fastingToday,
        history: fastingHistory,
        mealTimestamps: todayMealTs,
        currentMeal,
      },
      mounjaro: getMounjaroDay(log),
      ketone: getTodayKetone(),
    };
    // tick triggers re-read on focus/visibility/storage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);
  return { data, bumpTick };
}

// ─── TOKENS ──────────────────────────────────────────────────────
// Text-tier opacities tuned for low-brightness / dark-mode-in-bed
// readability. Was 0.62 / 0.55 — bumped 10 pp each so secondary +
// tertiary remain visible without losing the visual hierarchy.
export const C = {
  ink:   '#f4eee0',
  dim:   'rgba(244,238,224,0.78)',
  faint: 'rgba(244,238,224,0.66)',
  rule:  'rgba(244,238,224,0.12)',
  amber: '#f5b342',
  teal:  '#5fd1b9',
  coral: '#f08a6b',
  cream: '#e9dfc7',
  green: '#00e676',
  deepBlue: '#6ea8ff',
};
export const FS = "'Geist', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
export const FM = "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace";

// Native iOS time-wheel picker for the fasting-edit dialog. Uncontrolled
// (defaultValue + ref) — every parent re-render of a controlled value=
// rewrites the DOM attribute mid-spin, which iOS treats as an external
// change and dismisses the wheel. Refs hold the live value; the dialog
// reads them on Save.
//
// onHHMMChange is called on each change for callers wanting live state
// sync, but the input's own value attribute is never rewritten while
// the user is editing.
const HHMMSelect = React.forwardRef(function HHMMSelect({ value, onHHMMChange }, ref) {
  const [h, m] = (value || "00:00").split(":");
  const initVal = `${String(parseInt(h, 10) || 0).padStart(2, "0")}:${String(parseInt(m, 10) || 0).padStart(2, "0")}`;
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    get value() {
      const raw = inputRef.current?.value || "00:00";
      const [hh = "00", mm = "00"] = raw.split(":");
      const hN = Math.max(0, Math.min(23, parseInt(hh, 10) || 0));
      const mN = Math.max(0, Math.min(59, parseInt(mm, 10) || 0));
      return `${String(hN).padStart(2, "0")}:${String(mN).padStart(2, "0")}`;
    },
  }), []);
  return (
    <input
      ref={inputRef}
      type="time"
      defaultValue={initVal}
      onChange={(e) => onHHMMChange && onHHMMChange(e.target.value)}
      aria-label="Time"
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: C.ink, borderRadius: 10, padding: "12px 14px",
        fontFamily: FM, fontSize: 18, minHeight: 44,
        WebkitAppearance: "none", appearance: "none",
        flex: 1, textAlign: "center",
        letterSpacing: "1px",
      }}/>
  );
});

// ─── HOOKS ───────────────────────────────────────────────────────
// Returns true if motion should be reduced — checks user pref + OS hint.
function shouldReduceMotion() {
  try {
    const p = JSON.parse(localStorage.getItem("hayahPrefs") || "null");
    if (p?.motion?.reduce) return true;
  } catch {}
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

function useCountUp(target, dur = 1400, delay = 200) {
  const [v, setV] = useState(() => shouldReduceMotion() ? target : 0);
  useEffect(() => {
    if (shouldReduceMotion()) { setV(target); return; }
    let raf, tid = setTimeout(() => {
      let t0;
      const step = ts => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        setV(Math.round((1 - Math.pow(1 - p, 3)) * target));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); cancelAnimationFrame(raf); };
  }, [target, dur, delay]);
  return v;
}

function useRingFill(pct, delay = 300, dur = 1500) {
  const [f, setF] = useState(() => shouldReduceMotion() ? pct : 0);
  // Run the entrance 0→target animation only on mount. After that, jump
  // straight to new `pct` values so the SVG's stroke-dasharray transition
  // smoothly tweens between updates instead of snapping back to 0 each time
  // (which made the "Remaining" ring appear to reset on every meal log).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (shouldReduceMotion()) { setF(pct); mountedRef.current = true; return; }
    if (mountedRef.current) {
      setF(pct);
      return;
    }
    mountedRef.current = true;
    let raf, tid = setTimeout(() => {
      let t0;
      const step = ts => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        setF((1 - Math.pow(1 - p, 3)) * pct);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); cancelAnimationFrame(raf); };
  }, [pct, delay, dur]);
  return f;
}

// ─── GLOW RING ───────────────────────────────────────────────────
function GlowRing({ value, max, color, size = 140, sw = 10, ticks = false,
  glowBlur = 10, centerNode, delay = 300, uid = 'r' }) {
  const r = (size - sw) / 2, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const fill = useRingFill(Math.min(value / max, 1), delay) * circ;
  const endA = (fill / circ) * 2 * Math.PI - Math.PI / 2;
  const ex = cx + Math.cos(endA) * r, ey = cy + Math.sin(endA) * r;
  const tr = 'stroke-dasharray 1.5s cubic-bezier(0.25,0.46,0.45,0.94)';
  const kf = `@keyframes bg-${uid}{0%,100%{filter:drop-shadow(0 0 ${glowBlur}px ${color})}50%{filter:drop-shadow(0 0 ${glowBlur * 2.2}px ${color})}}`;
  const tickEls = [];
  if (ticks) for (let i = 0; i < 36; i++) {
    const a = (i / 36) * 2 * Math.PI - Math.PI / 2, big = i % 9 === 0;
    const r1 = r + sw / 2 + 3, r2 = r + sw / 2 + (big ? 8 : 5);
    tickEls.push(<line key={i}
      x1={cx + Math.cos(a) * r1} y1={cy + Math.sin(a) * r1}
      x2={cx + Math.cos(a) * r2} y2={cy + Math.sin(a) * r2}
      stroke={big ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)'}
      strokeWidth={big ? 1.4 : 0.7} />);
  }
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <style>{kf}</style>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{ overflow: 'visible', transform: 'rotate(-90deg)' }}>
        {tickEls}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color}
          strokeWidth={sw + glowBlur * 2} strokeDasharray={`${fill} ${circ}`}
          style={{ filter: `blur(${glowBlur}px)`, opacity: 0.2, transition: tr }} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeLinecap="round" strokeDasharray={`${fill} ${circ}`}
          style={{ animation: `bg-${uid} 3.2s ease-in-out infinite`, transition: tr }} />
        {fill > 8 && <circle cx={ex} cy={ey} r={sw / 2 + 1.5} fill={color}
          style={{ filter: `drop-shadow(0 0 6px ${color})` }} />}
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        {centerNode}
      </div>
    </div>
  );
}

// ─── LED METER ────────────────────────────────────────────────────
function LedMeter({ label, value, max, color, seg = 15, delay = 400 }) {
  const fill = useRingFill(Math.min(value / max, 1), delay, 1200);
  const disp = useCountUp(value, 1100, delay);
  const lit = Math.round(fill * seg);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <div style={{ fontFamily: FM, fontSize: 9, color, textTransform: 'uppercase', letterSpacing: '1.4px' }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: 2.5 }}>
        {Array.from({ length: seg }).map((_, i) => {
          const on = i < lit, pI = i / seg;
          const bc = pI > 0.75 ? '#ff6b6b' : pI > 0.5 ? C.amber : color;
          return <div key={i} style={{
            width: 28, height: 3, borderRadius: 2,
            background: on ? bc : 'rgba(255,255,255,0.07)',
            boxShadow: on ? `0 0 5px ${bc}` : 'none',
          }} />;
        })}
      </div>
      <div style={{ fontFamily: FM, fontSize: 13, color: C.ink, fontWeight: 600, lineHeight: 1 }}>
        {disp}<span style={{ fontSize: 8, color: C.dim }}>g</span>
      </div>
      <div style={{ fontFamily: FM, fontSize: 8, color: C.faint }}>/{max}g</div>
    </div>
  );
}

// ─── BURN CARD ────────────────────────────────────────────────────
function BurnCard({ total = 1338, active = 410, color = C.green }) {
  const dT = useCountUp(total, 1200, 600);
  const dA = useCountUp(active, 1000, 700);
  const pA = total > 0 ? active / total : 0;
  const fA = useRingFill(pA, 600, 1000);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 14, padding: '9px 12px',
    }}>
      <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
        <svg width={52} height={52} viewBox="0 0 52 52" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
          <circle cx={26} cy={26} r={20} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={6} />
          <circle cx={26} cy={26} r={20} fill="none" stroke={color} strokeWidth={6}
            strokeLinecap="round"
            strokeDasharray={`${fA * 2 * Math.PI * 20} ${2 * Math.PI * 20}`}
            style={{ transition: 'stroke-dasharray 1.2s ease', filter: `drop-shadow(0 0 5px ${color})` }} />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontFamily: FM, fontSize: 9, color,
        }}>
          {Math.round(pA * 100)}%
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 6,
          fontFamily: FS, fontWeight: 300, fontSize: 24, color: C.ink, letterSpacing: '-0.8px', lineHeight: 1,
        }}>
          {dT.toLocaleString()}
          <span style={{ fontFamily: FM, fontSize: 9, color: C.dim, letterSpacing: '1px' }}>KCAL BURNED</span>
        </div>
        <div style={{ marginTop: 7, display: 'flex', gap: 14, alignItems: 'center' }}>
          {[{ l: 'ACTIVE', v: dA, c: color }, { l: 'RESTING', v: total - active, c: C.faint }].map(({ l, v, c }) => (
            <div key={l}>
              <div style={{ fontFamily: FM, fontSize: 8, color: c, letterSpacing: '1.4px', textTransform: 'uppercase' }}>{l}</div>
              <div style={{ fontFamily: FM, fontSize: 11, color: C.ink, marginTop: 2 }}>
                {typeof v === 'number' ? v.toLocaleString() : v}<span style={{ fontSize: 7, color: C.dim }}>kcal</span>
              </div>
            </div>
          ))}
          <div style={{ flex: 1 }}>
            <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${fA * 100}%`, background: color, borderRadius: 4,
                transition: 'width 1.2s ease', boxShadow: `0 0 6px ${color}`,
              }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SLEEP CARD ───────────────────────────────────────────────────
function SleepCard({ totalMins = 412, deep = 40, rem = 98, light = 250, awake = 24,
  startLabel = '11:14P', endLabel = '6:06A', goalHours = 8 }) {
  const safeTotal = totalMins > 0 ? totalMins : 1;
  const stages = [
    { label: 'DEEP', mins: deep, color: '#6ea8ff', pct: deep / safeTotal },
    { label: 'REM', mins: rem, color: C.green, pct: rem / safeTotal },
    { label: 'LIGHT', mins: light, color: 'rgba(0,230,118,0.3)', pct: light / safeTotal },
    { label: 'AWAKE', mins: awake, color: 'rgba(255,255,255,0.18)', pct: awake / safeTotal },
  ];
  const totalFill = useRingFill(1, 800, 1200);
  const sleepH = useCountUp(Math.floor(totalMins / 60), 1000, 800);
  const sleepM = useCountUp(totalMins % 60, 1100, 850);

  let cursor = 0;
  const barSegs = stages.map(s => {
    const seg = { ...s, start: cursor };
    cursor += s.pct;
    return seg;
  });

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 14, padding: '9px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontFamily: FS, fontWeight: 300, fontSize: 24, color: C.ink, letterSpacing: '-0.8px', lineHeight: 1 }}>
            {sleepH}h {sleepM}m
          </span>
          <span style={{ fontFamily: FM, fontSize: 9, color: C.dim, letterSpacing: '1px' }}>/ {goalHours}H GOAL</span>
        </div>
        <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.8px' }}>{startLabel} – {endLabel}</span>
      </div>

      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1, marginBottom: 8 }}>
        {barSegs.map(s => (
          <div key={s.label} style={{
            flex: s.pct * totalFill,
            minWidth: 0,
            background: s.color,
            borderRadius: 2,
            transition: 'flex 1.4s cubic-bezier(0.25,0.46,0.45,0.94)',
            boxShadow: s.label === 'REM' || s.label === 'DEEP' ? `0 0 6px ${s.color}` : 'none',
          }} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {stages.map(s => (
          <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 6, height: 6, borderRadius: 6, background: s.color,
                boxShadow: s.label === 'DEEP' || s.label === 'REM' ? `0 0 5px ${s.color}` : 'none',
              }} />
              <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.2px', textTransform: 'uppercase' }}>{s.label}</span>
            </div>
            <span style={{ fontFamily: FM, fontSize: 10, color: C.ink, fontWeight: 600 }}>
              {s.mins >= 60 ? `${Math.floor(s.mins / 60)}h ${s.mins % 60}m` : `${s.mins}m`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── FASTING HELPERS ──────────────────────────────────────────────
// Thresholds come from `hayahPrefs.fasting.{ok,good,goal}Hours` and are
// passed into the visual components as props. fmtClock / fmtHM are
// imported from hayahPrefs.js so the Settings tab and Today share the
// exact same formatting + 12h/24h time-format support.

function fmtClock(ts, timeFmt) { return fmtClockPref(ts, timeFmt); }

function fastColor(h, thresholds = { ok: 12, good: 14, goal: 16 }) {
  if (h == null) return C.faint;
  if (h >= thresholds.goal) return C.green;
  if (h >= thresholds.good) return C.amber;
  if (h >= thresholds.ok)   return C.cream;
  return C.coral;
}
function dayLabel(dateStr) {
  // dateStr = YYYY-MM-DD; render local-date-safe, no UTC shift.
  const d = new Date(`${dateStr}T00:00:00`);
  return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
}
function calcFastStreak(history, goodHours = 14) {
  let n = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    const h = history[i]?.hours;
    if (h != null && h >= goodHours) n++;
    else break;
  }
  return n;
}

function dayHasMeals(log, ds) {
  const day = log[ds];
  if (!day || !day.meals) return false;
  return Object.values(day.meals).filter(Array.isArray).some(arr => arr.length > 0);
}

// Walks back from today counting consecutive days with at least one logged
// food item. If today isn't logged yet we start from yesterday so the
// streak survives an unlogged morning — it only breaks once a full day has
// passed with no entries.
function calcCalorieStreak(log) {
  let streak = 0;
  const d = new Date();
  if (!dayHasMeals(log, localDateStr(d))) d.setDate(d.getDate() - 1);
  while (dayHasMeals(log, localDateStr(d))) {
    streak++;
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

// ─── FASTING CARD (block 05) ──────────────────────────────────────
function FastingCard({ startTs, endTs, hours, source, inProgress, history = [], mealTimestamps = [],
  currentMeal = null,
  thresholds = { ok: 12, good: 14, goal: 16 }, timeFmt = "12h" }) {
  // Phase choice:
  //   eating  → hero = "since last meal" + stage band
  //   fasting → hero = traditional "in fast" hours
  // Eating is only chosen if we actually have a meal today AND the meal
  // is recent enough that the live counter is meaningful (< 24h).
  const inEatingPhase = currentMeal?.mode === "eating"
    && Number.isFinite(currentMeal?.hoursSinceLastMeal)
    && currentMeal.hoursSinceLastMeal < 24;

  // The hero numbers depend on phase. In eating mode we count up the
  // since-last-meal hours; in fasting mode we use the completed/live
  // fast `hours`.
  const heroHoursRaw = inEatingPhase ? currentMeal.hoursSinceLastMeal : (hours || 0);
  const heroH = useCountUp(Math.floor(heroHoursRaw), 1100, 600);
  const heroM = useCountUp(Math.round((heroHoursRaw % 1) * 60), 1100, 650);
  const fillNow = useRingFill(Math.min((hours || 0) / thresholds.goal, 1), 700, 1200);

  const noData = startTs == null && !inEatingPhase;
  const accent = fastColor(hours, thresholds);

  // 24h timeline. Day "anchor" = the local midnight that fast-start sits in
  // when fasting started yesterday evening; we render a 24h window centered
  // around the current "fast day" so the amber band reads naturally.
  // Simpler choice: render today's local 24h (00:00 → 24:00) and clamp the
  // fasting band to that window. The band may start at 0 if the fast began
  // before midnight.
  const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
  const dayEnd = dayStart.getTime() + 24 * 3600000;
  const nowTs = Date.now();
  const bandStart = startTs != null ? Math.max(startTs, dayStart.getTime()) : null;
  const bandEnd = (endTs ?? (inProgress ? nowTs : null));
  const bandEndClamped = bandEnd != null ? Math.min(bandEnd, dayEnd) : null;
  const pct = (ts) => Math.max(0, Math.min(1, (ts - dayStart.getTime()) / (24 * 3600000)));

  // Eating window (today): earliest → latest meal loggedAt
  const eatStart = mealTimestamps[0] ?? null;
  const eatEnd = mealTimestamps[mealTimestamps.length - 1] ?? null;
  const eatHours = (eatStart && eatEnd && eatEnd >= eatStart) ? (eatEnd - eatStart) / 3600000 : null;

  // Recent 7 days for the BEST/STREAK foot cells (history may carry up to 30)
  const recent7 = history.slice(-7);
  const longest7 = recent7.reduce((max, d) => (d.hours != null && d.hours > max ? d.hours : max), 0);
  const streak = calcFastStreak(recent7, thresholds.good);

  const startLbl = startTs ? fmtClock(startTs, timeFmt) : '—';
  const endLbl = endTs ? fmtClock(endTs, timeFmt) : (inProgress ? '···' : '—');
  const sourceLbl = source === 'lastMeal' ? 'last meal'
                  : source === 'bedtime+offset' ? 'sleep + offset'
                  : source === 'bedtime+1h' ? 'sleep + 1h'
                  : source === 'manual' ? 'manual override'
                  : '';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 14, padding: '9px 12px',
    }}>
      <style>{`@keyframes hayah-fast-pulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          {noData ? (
            <span style={{ fontFamily: FS, fontWeight: 300, fontSize: 24, color: C.dim, letterSpacing: '-0.8px', lineHeight: 1 }}>
              — —
            </span>
          ) : inEatingPhase ? (
            <>
              <span style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: 8,
                background: C.cream, boxShadow: `0 0 8px ${C.cream}99`,
                animation: 'hayah-fast-pulse 1.6s ease-in-out infinite',
                marginRight: 4, alignSelf: 'center',
              }} />
              <span style={{ fontFamily: FS, fontWeight: 300, fontSize: 24, color: C.ink, letterSpacing: '-0.8px', lineHeight: 1 }}>
                {heroH}h {String(heroM).padStart(2, '0')}m
              </span>
              <span style={{ fontFamily: FM, fontSize: 9, color: C.dim, letterSpacing: '1px' }}>
                · SINCE LAST MEAL
              </span>
            </>
          ) : (
            <>
              {inProgress && (
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: 8,
                  background: accent, boxShadow: `0 0 8px ${accent}`,
                  animation: 'hayah-fast-pulse 1.6s ease-in-out infinite',
                  marginRight: 4, alignSelf: 'center',
                }} />
              )}
              <span style={{ fontFamily: FS, fontWeight: 300, fontSize: 24, color: C.ink, letterSpacing: '-0.8px', lineHeight: 1 }}>
                {heroH}h {String(heroM).padStart(2, '0')}m
              </span>
              <span style={{ fontFamily: FM, fontSize: 9, color: C.dim, letterSpacing: '1px' }}>
                / {thresholds.goal}H {inProgress ? '· FASTING' : 'WINDOW'}
              </span>
            </>
          )}
        </div>
        <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.8px' }}>
          {inEatingPhase ? (currentMeal.lastMealName || (currentMeal.lastMealSlot ? String(currentMeal.lastMealSlot).toUpperCase() : 'MEAL')).slice(0, 18) : `${startLbl} – ${endLbl}`}
        </span>
      </div>

      {/* Secondary line: in eating mode, show the just-completed fast.
          In fasting mode, show the same start/end times more loudly. */}
      {!noData && inEatingPhase && hours != null && (
        <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.8px', marginBottom: 10 }}>
          LAST FAST · {fmtHM(hours)} · ENDED {endLbl}
        </div>
      )}
      {!noData && !inEatingPhase && (<div style={{ marginBottom: 4 }} />)}

      {/* STAGE BAND — shows current physiological zone with progress to next.
          Only renders when we have live elapsed hours to place the marker. */}
      {(() => {
        const liveH = inEatingPhase ? currentMeal.hoursSinceLastMeal : (inProgress ? hours : null);
        if (liveH == null || !Number.isFinite(liveH)) return null;
        const stage = (currentMeal?.stage) || (FASTING_STAGES.find(s => liveH >= s.h0 && liveH < s.h1) || FASTING_STAGES[FASTING_STAGES.length - 1]);
        const next  = (currentMeal?.nextStage) || (FASTING_STAGES.find(s => s.h0 > liveH) || null);
        const stageColors = {
          digesting: C.cream, glycogen: C.amber, ketosis: C.green,
          autophagy: C.green, deep: '#7AE7C7',
        };
        const sc = stageColors[stage.slug] || C.amber;
        // 0–24h band scale, marker = current liveH
        const markerPct = Math.max(0, Math.min(1, liveH / 24)) * 100;
        return (
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontFamily: FM, fontSize: 9, color: sc, letterSpacing: '1.2px', textTransform: 'uppercase', fontWeight: 600 }}>
                {stage.label}
              </span>
              {next && Number.isFinite(currentMeal?.hoursToNextStage) && (
                <span style={{ fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: '1px' }}>
                  {fmtHM(currentMeal.hoursToNextStage)} → {next.label}
                </span>
              )}
            </div>
            <div style={{
              position: 'relative', height: 6, borderRadius: 3, overflow: 'visible',
              background: 'rgba(255,255,255,0.04)',
            }}>
              {/* Stage segments */}
              {FASTING_STAGES.map((s, i) => {
                const left = (s.h0 / 24) * 100;
                const right = (Math.min(s.h1, 24) / 24) * 100;
                const w = right - left;
                const active = s.slug === stage.slug;
                const col = stageColors[s.slug] || C.faint;
                return (
                  <div key={s.slug} style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${left}%`, width: `${w}%`,
                    background: active ? `${col}aa` : `${col}26`,
                    borderRadius: i === 0 ? '3px 0 0 3px' : (i === FASTING_STAGES.length - 1 ? '0 3px 3px 0' : 0),
                    transition: 'background .35s ease',
                  }} />
                );
              })}
              {/* Now marker */}
              <div style={{
                position: 'absolute', top: -3, bottom: -3,
                left: `calc(${markerPct}% - 1px)`, width: 2,
                background: sc, borderRadius: 2,
                boxShadow: `0 0 6px ${sc}`,
              }} />
            </div>
          </div>
        );
      })()}

      {/* 24h timeline strip */}
      <div style={{
        position: 'relative', height: 14, borderRadius: 4, overflow: 'hidden',
        background: 'rgba(255,255,255,0.05)', marginBottom: 8,
      }}>
        {/* 6h tickmarks */}
        {[0.25, 0.5, 0.75].map(p => (
          <div key={p} style={{
            position: 'absolute', top: 0, bottom: 0, left: `${p * 100}%`,
            width: 1, background: 'rgba(255,255,255,0.06)',
          }} />
        ))}
        {/* Fasting band */}
        {bandStart != null && bandEndClamped != null && bandEndClamped > bandStart && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${pct(bandStart) * 100}%`,
            width: `${(pct(bandEndClamped) - pct(bandStart)) * 100 * fillNow}%`,
            background: `linear-gradient(90deg, ${accent}aa, ${accent})`,
            boxShadow: `0 0 8px ${accent}88`,
            transition: 'width 1.2s cubic-bezier(0.25,0.46,0.45,0.94)',
          }} />
        )}
        {/* Eating window markers */}
        {mealTimestamps.map((t, i) => (
          <div key={i} style={{
            position: 'absolute', top: 2, bottom: 2,
            left: `calc(${pct(t) * 100}% - 1px)`,
            width: 2, background: C.cream, borderRadius: 1,
            boxShadow: `0 0 4px ${C.cream}`,
          }} />
        ))}
        {/* Now cursor */}
        <div style={{
          position: 'absolute', top: -2, bottom: -2,
          left: `${pct(nowTs) * 100}%`,
          width: 1, background: C.green, opacity: 0.85,
          boxShadow: `0 0 6px ${C.green}`,
        }} />
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontFamily: FM, fontSize: 7.5, color: C.faint, letterSpacing: '1px', marginBottom: 8,
      }}>
        <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
      </div>

      {/* Foot row — 4 stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {[
          { label: 'WINDOW',  val: noData ? '—' : fmtHM(hours),     dot: accent },
          { label: 'EATING',  val: eatHours != null ? fmtHM(eatHours) : '—', dot: C.cream },
          { label: 'BEST 7D', val: longest7 > 0 ? fmtHM(longest7)   : '—', dot: C.green },
          { label: 'STREAK',  val: streak > 0 ? `${streak}d`       : '—', dot: C.amber },
        ].map(s => (
          <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 6, height: 6, borderRadius: 6, background: s.dot,
                boxShadow: s.dot === C.amber || s.dot === C.green ? `0 0 5px ${s.dot}` : 'none',
              }} />
              <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.2px', textTransform: 'uppercase' }}>{s.label}</span>
            </div>
            <span style={{ fontFamily: FM, fontSize: 10, color: C.ink, fontWeight: 600 }}>{s.val}</span>
          </div>
        ))}
      </div>

      {/* Source attribution */}
      {!noData && !inEatingPhase && (
        <div style={{
          marginTop: 8, fontFamily: FM, fontSize: 8, color: C.faint,
          letterSpacing: '1.2px', textTransform: 'uppercase',
        }}>
          {inProgress ? 'STARTED' : 'WINDOW'} {sourceLbl ? `· FROM ${sourceLbl}` : ''}
        </div>
      )}
      {!noData && inEatingPhase && (() => {
        const ew = currentMeal?.eatingWindow;
        if (ew?.state === 'open' && Number.isFinite(ew?.msToCutoff) && ew.msToCutoff > 0) {
          return (
            <div style={{
              marginTop: 8, fontFamily: FM, fontSize: 8, color: C.faint,
              letterSpacing: '1.2px', textTransform: 'uppercase',
            }}>
              EATING WINDOW · CLOSES IN {fmtHM(ew.msToCutoff / 3600000)} · NEXT FAST BEGINS {fmtClock(ew.cutoffTs, timeFmt)}
            </div>
          );
        }
        return (
          <div style={{
            marginTop: 8, fontFamily: FM, fontSize: 8, color: C.faint,
            letterSpacing: '1.2px', textTransform: 'uppercase',
          }}>
            EATING WINDOW · OPEN · MEALS TODAY {currentMeal?.mealsTodayCount ?? '—'}
          </div>
        );
      })()}
      {noData && (
        <div style={{
          marginTop: 8, fontFamily: FM, fontSize: 8, color: C.faint,
          letterSpacing: '1px',
        }}>
          Log a meal in Ghidha to start the auto-detected window.
        </div>
      )}
    </div>
  );
}

// ─── FASTING DETAIL OVERLAY ───────────────────────────────────────
function tsToHHMM(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}
function hhmmOnDate(dateStr, hhmm) {
  if (!dateStr || !hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(`${dateStr}T00:00:00`);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}
function prevDateStr(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() - 1);
  return localDateStr(d);
}

function FastingDetailOverlay({ open, onClose, fasting,
  thresholds = { ok: 12, good: 14, goal: 16 }, timeFmt = "12h",
  onEditOverride, onResetOverride, showBenefitsTimeline = true }) {
  const closeBtnRef = useRef(null);
  const [highlightDate, setHighlightDate] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [editNote, setEditNote] = useState("");
  // Refs hold the live <input> values for the time fields. The native iOS
  // time-picker wheel fires `input` events for every wheel tick — feeding
  // those back into a controlled `value=` rewrites the attribute mid-pick
  // and iOS dismisses the picker. Use uncontrolled inputs (defaultValue +
  // ref) and read on Save instead. Must live above the early return below
  // so hook order stays stable across renders.
  const editStartRef = useRef(null);
  const editEndRef = useRef(null);
  // HHMMSelect imperative handles. Declared above the `if (!open) return`
  // below so rules-of-hooks stays happy across renders.
  const startSelectRef = useRef(null);
  const endSelectRef = useRef(null);

  // Auto-focus the close button on first open only. The previous version
  // depended on [open, onClose], but onClose is a new function reference
  // every parent render — so every state update inside the dialog (typing
  // in a time input) re-ran the effect and stole focus back to the close
  // button, dismissing the iOS keyboard. Now the effect only fires on
  // open going false → true, never on subsequent re-renders.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open) { wasOpenRef.current = false; return; }
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      const t = setTimeout(() => {
        // Don't steal focus from a typing user — only auto-focus if
        // nothing else has taken focus inside the overlay yet.
        if (!isUserEditing()) closeBtnRef.current?.focus();
      }, 50);
      return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
    }
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !fasting) return null;
  const { startTs, endTs, hours, source, inProgress, history = [] } = fasting;
  const accent = fastColor(hours, thresholds);

  // Slice for 7D and 30D stats
  const last7 = history.slice(-7);
  const last30 = history.slice(-30);
  const prev30 = history.slice(-60, -30);
  const knownIn = arr => arr.filter(d => d.hours != null);
  const avg7 = knownIn(last7).length ? knownIn(last7).reduce((s,d)=>s+d.hours,0)/knownIn(last7).length : null;
  const avg30 = knownIn(last30).length ? knownIn(last30).reduce((s,d)=>s+d.hours,0)/knownIn(last30).length : null;
  const avgPrev30 = knownIn(prev30).length ? knownIn(prev30).reduce((s,d)=>s+d.hours,0)/knownIn(prev30).length : null;
  const trendDelta = (avg30 != null && avgPrev30 != null) ? (avg30 - avgPrev30) : null;
  const longest = knownIn(history).length ? knownIn(history).reduce((m,d)=>Math.max(m,d.hours), 0) : null;
  const shortest = knownIn(history).length ? knownIn(history).reduce((m,d)=>Math.min(m,d.hours), Infinity) : null;
  const goalHits7 = knownIn(last7).filter(d => d.hours >= thresholds.goal).length;
  const goalHits30 = knownIn(last30).filter(d => d.hours >= thresholds.goal).length;

  // Bar chart geometry — last 14 days fits nicely; for >14 we render last 14
  const chartHistory = history.slice(-14);
  const W = 340, H = 150, padL = 28, padR = 8, padT = 10, padB = 22;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const yMax = 24;
  const yFor = (h) => padT + innerH - (h / yMax) * innerH;
  const slot = innerW / chartHistory.length;
  const barW = slot * 0.62;

  const sourceLbl = source === 'lastMeal' ? 'from last meal yesterday'
                  : source === 'bedtime+offset' ? 'from bedtime + offset (no meal logged yesterday)'
                  : source === 'manual' ? 'from manual override'
                  : '';

  // Milestone timeline geometry
  const liveH = hours == null ? 0 : Math.min(hours, 24);
  const nm = nextMilestone(liveH);

  const startEdit = (d) => {
    setEditingDate(d.date);
    setEditStart(d.startTs ? tsToHHMM(d.startTs) : "");
    setEditEnd(d.endTs ? tsToHHMM(d.endTs) : "");
    setEditNote(d.note || "");
  };
  const saveEdit = () => {
    if (!editingDate) return;
    // Read live from the uncontrolled HHMMSelect refs so the displayed
    // (untyped) value wins over the lagging state copy — state can lag
    // because we don't re-render on every keystroke (that would rewrite
    // the input value attribute and dismiss the iOS keyboard).
    const startVal = startSelectRef.current?.value || editStart;
    const endVal   = endSelectRef.current?.value   || editEnd;
    const patch = { note: editNote, deleted: false };
    // Always write startTs / endTs explicitly so clearing a field removes
    // any prior override. null = "fall back to auto-derived from meal log",
    // which for endTs means the fast remains in-progress (ongoing).
    patch.startTs = startVal ? hhmmOnDate(prevDateStr(editingDate), startVal) : null;
    patch.endTs   = endVal   ? hhmmOnDate(editingDate, endVal)                : null;
    onEditOverride?.(editingDate, patch);
    setEditingDate(null);
  };
  // HHMMSelect lives at module scope below — defining it inside this
  // render makes React see a brand-new component type on every state
  // change, which unmounts and remounts the inputs and drops focus
  // (the keyboard popped up then dismissed instantly).
  const deleteRow = (date) => onEditOverride?.(date, { deleted: true });
  const resetRow = (date) => onResetOverride?.(date);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Fasting detail"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        fontFamily: FS, color: C.ink,
        background: 'radial-gradient(ellipse at 50% -5%, rgba(245,179,66,0.10), transparent 50%), #070b09',
        overflowY: 'auto',
        animation: 'hayah-hud-drop 220ms cubic-bezier(0.22,1,0.36,1) both',
      }}
    >
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(245,179,66,0.025) 0 1px, transparent 1px 4px)',
      }} />

      <div style={{ position: 'relative', padding: 'calc(12px + env(safe-area-inset-top)) 16px calc(96px + env(safe-area-inset-bottom))' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close fasting detail"
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              color: C.ink, width: 32, height: 32, borderRadius: 16,
              fontFamily: FM, fontSize: 16, lineHeight: 1, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >←</button>
          <span style={{ fontFamily: FM, fontSize: 10, color: C.amber, letterSpacing: '2.4px', textTransform: 'uppercase', fontWeight: 600 }}>
            FASTING · DETAIL
          </span>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(245,179,66,0.4), transparent)' }} />
        </div>

        {/* Hero */}
        <div style={{
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 16, padding: '16px 16px 14px', marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: FS, fontWeight: 200, fontSize: 56, color: accent,
              letterSpacing: '-2px', lineHeight: 1,
              textShadow: `0 0 24px ${accent}55`,
            }}>
              {hours != null ? fmtHM(hours) : '—'}
            </span>
            {inProgress && (
              <span style={{ fontFamily: FM, fontSize: 10, color: accent, letterSpacing: '1.6px', textTransform: 'uppercase' }}>
                · FASTING NOW
              </span>
            )}
          </div>
          <div style={{ marginTop: 10, fontFamily: FM, fontSize: 11, color: C.dim, letterSpacing: '0.5px' }}>
            {startTs ? `${fmtClock(startTs, timeFmt)} ${startTs && endTs ? '→' : '→ ···'} ${endTs ? fmtClock(endTs, timeFmt) : (inProgress ? 'still fasting' : '—')}` : 'No data yet'}
          </div>
          {sourceLbl && (
            <div style={{ marginTop: 4, fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '1.2px', textTransform: 'uppercase' }}>
              start {sourceLbl}
            </div>
          )}

          {/* Smart break-fast suggestion */}
          {inProgress && hours != null && hours >= thresholds.goal && (
            <div style={{
              marginTop: 12, padding: '8px 10px',
              background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.25)',
              borderRadius: 10, fontFamily: FM, fontSize: 10, color: C.green, letterSpacing: '0.4px',
            }}>
              ✓ Goal hit. Break with a protein-forward meal — your body's ready.
            </div>
          )}

          {/* Milestone timeline */}
          {showBenefitsTimeline && startTs && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: '1.6px', textTransform: 'uppercase', marginBottom: 6 }}>
                Benefits timeline
              </div>
              <div style={{ position: 'relative', height: 38 }}>
                {/* baseline */}
                <div style={{ position: 'absolute', top: 18, left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                {FASTING_MILESTONES.map(m => {
                  const x = (m.h / 24) * 100;
                  const passed = liveH >= m.h;
                  const isNext = nm && nm.h === m.h;
                  return (
                    <div key={m.h} style={{
                      position: 'absolute', left: `${x}%`, top: 0, transform: 'translateX(-50%)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                    }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: 8, marginTop: 14,
                        background: passed ? C.amber : isNext ? 'rgba(245,179,66,0.4)' : 'rgba(255,255,255,0.15)',
                        boxShadow: passed ? `0 0 6px ${C.amber}` : 'none',
                        border: isNext ? `1px solid ${C.amber}` : 'none',
                      }} />
                      <div style={{
                        marginTop: 2, fontFamily: FM, fontSize: 7, color: passed ? C.amber : C.faint,
                        letterSpacing: '0.5px',
                      }}>{m.h}h</div>
                    </div>
                  );
                })}
                {/* Live cursor */}
                {liveH > 0 && (
                  <div style={{
                    position: 'absolute', left: `${(liveH / 24) * 100}%`, top: 8, bottom: 8,
                    width: 2, background: accent, transform: 'translateX(-50%)',
                    boxShadow: `0 0 6px ${accent}`,
                  }} />
                )}
              </div>
              {nm && (
                <div style={{ marginTop: 6, fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.3px' }}>
                  Next: <span style={{ color: C.amber }}>{nm.h}h · {nm.label}</span>
                  {inProgress && hours != null && (
                    <> · in {fmtHM(nm.h - liveH)}</>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bar chart */}
        <div style={{ marginBottom: 14 }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6,
          }}>
            <span style={{ fontFamily: FM, fontSize: 9, color: C.amber, letterSpacing: '1.8px', textTransform: 'uppercase' }}>14-DAY HISTORY</span>
            <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.8px' }}>
              {goalHits7}/7 · {goalHits30}/30 hit {thresholds.goal}h
            </span>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 14, padding: '10px 8px',
          }}>
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', overflow: 'visible' }}>
              {/* Y axis labels + reference lines */}
              {[0, 8, thresholds.ok, thresholds.good, thresholds.goal, 24].map(y => (
                <g key={y}>
                  <line
                    x1={padL} x2={W - padR} y1={yFor(y)} y2={yFor(y)}
                    stroke={y === thresholds.goal ? 'rgba(0,230,118,0.35)'
                          : y === thresholds.good ? 'rgba(233,223,199,0.20)'
                          : 'rgba(255,255,255,0.05)'}
                    strokeDasharray={y === thresholds.goal || y === thresholds.good ? '3 4' : ''}
                    strokeWidth={1}
                  />
                  <text x={padL - 4} y={yFor(y) + 3} textAnchor="end"
                    fontFamily="ui-monospace, monospace" fontSize="8"
                    fill={y === thresholds.goal ? C.green : y === thresholds.good ? C.cream : C.faint}>
                    {y}h
                  </text>
                </g>
              ))}
              {/* Bars */}
              {chartHistory.map((d, i) => {
                const x = padL + i * slot + (slot - barW) / 2;
                const h = d.hours != null ? d.hours : 0;
                const yTop = yFor(h);
                const isToday = i === chartHistory.length - 1;
                const isHi = highlightDate === d.date;
                const col = d.hours == null ? 'rgba(255,255,255,0.08)' : fastColor(d.hours, thresholds);
                return (
                  <g key={d.date}
                    onClick={() => setHighlightDate(d.date)}
                    style={{ cursor: d.hours != null ? 'pointer' : 'default' }}>
                    <rect
                      x={x} y={d.hours != null ? yTop : padT + innerH - 3}
                      width={barW} height={d.hours != null ? Math.max(2, padT + innerH - yTop) : 3}
                      rx={2}
                      fill={col}
                      opacity={d.hours == null ? 1 : (isHi ? 1 : 0.85)}
                      style={{
                        filter: d.hours != null && (isHi || d.hours >= thresholds.good) ? `drop-shadow(0 0 4px ${col})` : 'none',
                        transition: 'opacity 0.2s ease',
                      }}
                    />
                    {isToday && (
                      <rect x={x - 1} y={padT - 2} width={barW + 2} height={innerH + 6}
                        fill="none" stroke="rgba(255,255,255,0.15)" strokeDasharray="2 3" rx={3} />
                    )}
                    <text x={x + barW / 2} y={H - 8} textAnchor="middle"
                      fontFamily="ui-monospace, monospace" fontSize="8"
                      fill={isToday ? C.amber : C.faint}
                      letterSpacing="1px">
                      {dayLabel(d.date).slice(0,1)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Stats panel */}
        <div style={{
          display: 'flex', gap: 12, marginBottom: 14,
          background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 14, padding: '12px 14px', flexWrap: 'wrap',
        }}>
          <SyncCell label="7D AVG"   value={avg7 != null ? fmtHM(avg7) : '—'}      color={C.amber} />
          <div style={{ width: 1, background: 'rgba(255,255,255,0.07)' }} />
          <SyncCell label="30D AVG"  value={avg30 != null ? fmtHM(avg30) : '—'}    color={C.cream} />
          {trendDelta != null && (
            <span style={{
              fontFamily: FM, fontSize: 10, fontWeight: 700,
              color: trendDelta > 0 ? C.green : C.coral, letterSpacing: '0.3px',
              alignSelf: 'center', marginLeft: -6,
            }}>
              {trendDelta > 0 ? '▲' : '▼'} {fmtHM(Math.abs(trendDelta))}
            </span>
          )}
          <div style={{ width: 1, background: 'rgba(255,255,255,0.07)' }} />
          <SyncCell label="LONGEST"  value={longest != null ? fmtHM(longest) : '—'} color={C.green} />
          <div style={{ width: 1, background: 'rgba(255,255,255,0.07)' }} />
          <SyncCell label="SHORTEST" value={shortest != null && Number.isFinite(shortest) ? fmtHM(shortest) : '—'} color={C.coral} />
        </div>

        {/* Day list */}
        <div style={{ marginBottom: 14 }}>
          <span style={{ fontFamily: FM, fontSize: 9, color: C.amber, letterSpacing: '1.8px', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
            BREAKDOWN
          </span>
          <div style={{
            background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 14, overflow: 'hidden',
          }}>
            {history.slice().reverse().map((d, i, arr) => {
              const isHi = highlightDate === d.date;
              const isToday = i === 0;
              const isDeleted = d.override?.deleted;
              const hasOverride = d.override && !d.override.deleted && (d.override.startTs || d.override.endTs);
              const col = d.hours == null ? C.dim : fastColor(d.hours, thresholds);
              const isEditing = editingDate === d.date;
              return (
                <div key={d.date}
                  style={{
                    background: isHi ? 'rgba(245,179,66,0.07)' : 'transparent',
                    borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    transition: 'background 0.18s ease',
                    opacity: isDeleted ? 0.45 : 1,
                  }}>
                  <div
                    onClick={() => setHighlightDate(d.date)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}>
                    <div style={{ minWidth: 38 }}>
                      <div style={{ fontFamily: FM, fontSize: 10, color: isToday ? C.amber : C.ink, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', textDecoration: isDeleted ? 'line-through' : 'none' }}>
                        {isToday ? 'TODAY' : dayLabel(d.date).toUpperCase()}
                      </div>
                      <div style={{ fontFamily: FM, fontSize: 7.5, color: C.faint, letterSpacing: '0.6px', marginTop: 1 }}>
                        {d.date.slice(5)}
                        {hasOverride && <span style={{ marginLeft: 4, color: C.amber }}>·EDITED</span>}
                      </div>
                    </div>
                    <div style={{
                      fontFamily: FM, fontSize: 12, color: col, fontWeight: 700,
                      minWidth: 64, letterSpacing: '-0.3px',
                      textDecoration: isDeleted ? 'line-through' : 'none',
                    }}>
                      {isDeleted ? 'HIDDEN' : (d.hours != null ? fmtHM(d.hours) : '—')}
                    </div>
                    <div style={{ flex: 1, height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${isDeleted ? 0 : Math.min(100, ((d.hours || 0) / 24) * 100)}%`,
                        background: col,
                        boxShadow: d.hours != null && d.hours >= thresholds.good ? `0 0 5px ${col}` : 'none',
                        borderRadius: 4,
                      }} />
                    </div>
                    <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.4px', minWidth: 96, textAlign: 'right' }}>
                      {d.startTs ? `${fmtClock(d.startTs, timeFmt)} → ${d.endTs ? fmtClock(d.endTs, timeFmt) : (d.inProgress ? '···' : '—')}` : '—'}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); isEditing ? setEditingDate(null) : startEdit(d); }}
                      aria-label="Edit fast"
                      style={{
                        background: 'transparent', border: 'none', color: C.faint, cursor: 'pointer',
                        padding: '4px 6px', fontFamily: FM, fontSize: 12, lineHeight: 1,
                      }}>⋯</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer explainer */}
        <div style={{
          fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: '0.6px',
          lineHeight: 1.6, padding: '8px 4px 0',
        }}>
          Auto-derived from your meal log. <span style={{ color: C.cream }}>Start</span> = last non-tolerated meal of the previous day. <span style={{ color: C.cream }}>End</span> = first non-tolerated meal of the day. Drinks on the allow-list (coffee, tea, water) and items below the calorie tolerance don't end your fast. Tap <span style={{ color: C.amber }}>⋯</span> on any row to edit or delete. Targets: {thresholds.ok}h ok · {thresholds.good}h good · {thresholds.goal}h ideal.
        </div>
      </div>

      {/* Edit modal — pinned to top of viewport so iOS keyboard (which
          rises from the bottom) never competes with the inputs. Lives
          outside the day-list's scroll container so iOS's auto-scroll-
          into-view never moves it. */}
      {editingDate && (() => {
        const row = history.find(h => h.date === editingDate) || { date: editingDate, override: null };
        const hasOv = row.override && !row.override.deleted && (row.override.startTs || row.override.endTs);
        const isDel = !!row.override?.deleted;
        // Portal the modal to document.body. Inside the natural app tree,
        // iOS WKWebView's viewport adjustments for the on-screen keyboard
        // translate the containing block of fixed-position descendants,
        // which iOS treats as a layout invalidation reason to blur the
        // focused input. Hoisting to the body sidesteps that.
        return createPortal((
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Edit fasting day"
            // Intentionally no backdrop tap-to-close: iOS dismisses input
            // keyboards / picker overlays by registering a tap somewhere
            // outside their bounds. That dismiss-tap kept hitting this
            // backdrop and closing the whole dialog mid-edit. Close is
            // explicit via × / Cancel.
            style={{
              position: 'fixed', inset: 0, zIndex: 70,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              paddingTop: 'calc(24px + env(safe-area-inset-top))',
            }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              width: 'min(420px, calc(100% - 24px))',
              background: '#0a0a0e',
              border: '1px solid rgba(245,179,66,0.3)',
              borderRadius: 16,
              boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
              padding: 16,
              fontFamily: FS, color: C.ink,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontFamily: FM, fontSize: 10, color: C.amber, letterSpacing: '2.2px', textTransform: 'uppercase', fontWeight: 700 }}>
                  EDIT · {row.date}
                </span>
                <button onClick={() => setEditingDate(null)} aria-label="Close"
                  style={{ background: 'transparent', border: 'none', color: C.faint, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}>×</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase', flex: '0 0 60px' }}>Start</label>
                  <HHMMSelect key={`start-${editingDate}`} ref={startSelectRef} value={editStart} onHHMMChange={setEditStart}/>
                  <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, minWidth: 40, textAlign: 'right' }}>{prevDateStr(row.date).slice(5)}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase', flex: '0 0 60px' }}>End</label>
                  <HHMMSelect key={`end-${editingDate}`} ref={endSelectRef} value={editEnd} onHHMMChange={setEditEnd}/>
                  <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, minWidth: 40, textAlign: 'right' }}>{row.date.slice(5)}</span>
                </div>
                {!editEnd && (
                  <div style={{ fontFamily: FM, fontSize: 9, color: C.amber, letterSpacing: '1.2px', marginLeft: 68 }}>
                    End blank → fast treated as ongoing
                  </div>
                )}
                <textarea value={editNote} onChange={e => setEditNote(e.target.value)} placeholder="Note (optional)" rows={2}
                  style={{
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                    color: C.ink, borderRadius: 10, padding: '10px 14px',
                    fontFamily: FM, fontSize: 16, resize: 'vertical',
                    WebkitAppearance: 'none',
                  }} />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                <button onClick={saveEdit}
                  style={{ flex: 1, minWidth: 90, padding: '12px 14px', borderRadius: 10, border: 'none',
                    background: C.amber, color: '#1a0f00', fontFamily: FM, fontSize: 11, fontWeight: 700, letterSpacing: '1.4px', cursor: 'pointer', textTransform: 'uppercase' }}>
                  Save
                </button>
                {(hasOv || isDel) && (
                  <button onClick={() => { resetRow(row.date); setEditingDate(null); }}
                    style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)',
                      background: 'transparent', color: C.ink, fontFamily: FM, fontSize: 11, fontWeight: 700, letterSpacing: '1.4px', cursor: 'pointer', textTransform: 'uppercase' }}>
                    Reset
                  </button>
                )}
                <button onClick={() => { deleteRow(row.date); setEditingDate(null); }}
                  style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(240,138,107,0.4)',
                    background: 'rgba(240,138,107,0.08)', color: C.coral, fontFamily: FM, fontSize: 11, fontWeight: 700, letterSpacing: '1.4px', cursor: 'pointer', textTransform: 'uppercase' }}>
                  Delete
                </button>
                <button onClick={() => setEditingDate(null)}
                  style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)',
                    background: 'transparent', color: C.faint, fontFamily: FM, fontSize: 11, fontWeight: 700, letterSpacing: '1.4px', cursor: 'pointer', textTransform: 'uppercase' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ), document.body);
      })()}
    </div>
  );
}

// ─── TAP WRAPPER ──────────────────────────────────────────────────
export function Tap({ onTap, label, children, style }) {
  if (!onTap) return <div style={style}>{children}</div>;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={onTap}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); } }}
      style={{ cursor: 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', ...style }}
    >
      {children}
    </div>
  );
}

// ─── ORBIT Y-CLUSTER (Signal palette) ────────────────────────────
function OrbitCluster({ intake = 752, intakeGoal = 2000, burned = 1338, remaining = 1248,
  onIntake, onBurned, onRemaining }) {
  const kcalIn = useCountUp(intake, 1400, 300);
  const kcalBurn = useCountUp(burned, 1400, 420);
  const overGoal = remaining < 0;
  const kcalRem = useCountUp(Math.abs(remaining), 1400, 540);
  const W = 358, H = 182;
  const cx = 179, cy = 81, lx = 54, ly = 134, rx = 304, ry = 134;
  const intakePct = intakeGoal > 0 ? Math.round((intake / intakeGoal) * 100) : 0;

  return (
    <div style={{ position: 'relative', width: W, height: H, margin: '0 auto', maxWidth: '100%' }}>
      <svg style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}
        width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <line x1={cx} y1={cy} x2={lx} y2={ly}
          stroke="rgba(0,230,118,0.2)" strokeWidth={1} strokeDasharray="4 6" />
        <line x1={cx} y1={cy} x2={rx} y2={ry}
          stroke="rgba(245,179,66,0.2)" strokeWidth={1} strokeDasharray="4 6" />
        <line x1={lx} y1={ly} x2={rx} y2={ry}
          stroke="rgba(255,255,255,0.07)" strokeWidth={1} strokeDasharray="5 9" />
        <ellipse cx={cx} cy={cy} rx={76} ry={76}
          fill="none" stroke="rgba(0,230,118,0.04)" strokeWidth={1} />
      </svg>

      <Tap onTap={onIntake} label="Open calorie breakdown" style={{ position: 'absolute', top: 6, left: cx - 75 }}>
        <GlowRing value={intake} max={intakeGoal} color={C.amber} size={150} sw={10}
          ticks={true} uid="v3-a" glowBlur={12} delay={300}
          centerNode={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '1.6px', textTransform: 'uppercase' }}>INTAKE</div>
              <div style={{ fontFamily: FM, fontSize: 32, color: C.amber, fontWeight: 600, letterSpacing: '-0.6px', lineHeight: 1, marginTop: 2 }}>
                {kcalIn.toLocaleString()}
              </div>
              <div style={{ fontFamily: FM, fontSize: 9, color: C.dim, letterSpacing: '0.6px', marginTop: 2 }}>{intakePct}% · {intakeGoal.toLocaleString()}</div>
            </div>
          } />
      </Tap>

      <Tap onTap={onBurned} label="Open burned breakdown" style={{ position: 'absolute', top: ly - 42, left: lx - 42 }}>
        <GlowRing value={burned} max={intakeGoal} color={C.green} size={84} sw={6}
          uid="v3-b" glowBlur={9} delay={450}
          centerNode={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FM, fontSize: 7.5, color: C.faint, letterSpacing: '1.2px', textTransform: 'uppercase', lineHeight: 1 }}>BURNED</div>
              <div style={{ fontFamily: FM, fontSize: 18, color: C.ink, fontWeight: 600, letterSpacing: '-0.3px', lineHeight: 1.1, marginTop: 2 }}>
                {kcalBurn.toLocaleString()}
              </div>
              <div style={{ fontFamily: FM, fontSize: 7.5, color: C.green, letterSpacing: '0.5px' }}>kcal</div>
            </div>
          } />
      </Tap>

      <Tap onTap={onRemaining} label={overGoal ? "Open calorie overage" : "Open remaining budget"} style={{ position: 'absolute', top: ry - 42, left: rx - 42 }}>
        <GlowRing value={overGoal ? intakeGoal : Math.max(0, remaining)} max={intakeGoal} color={C.coral} size={84} sw={6}
          uid="v3-c" glowBlur={overGoal ? 13 : 9} delay={550}
          centerNode={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FM, fontSize: 7.5, color: overGoal ? C.coral : C.faint, letterSpacing: '1.2px', textTransform: 'uppercase', lineHeight: 1, fontWeight: overGoal ? 700 : 400 }}>{overGoal ? 'OVER' : 'LEFT'}</div>
              <div style={{ fontFamily: FM, fontSize: 18, color: overGoal ? C.coral : C.ink, fontWeight: 600, letterSpacing: '-0.3px', lineHeight: 1.1, marginTop: 2 }}>
                {overGoal ? '+' : ''}{kcalRem.toLocaleString()}
              </div>
              <div style={{ fontFamily: FM, fontSize: 7.5, color: C.coral, letterSpacing: '0.5px' }}>kcal</div>
            </div>
          } />
      </Tap>
    </div>
  );
}

// ─── STEPS WIDGET ────────────────────────────────────────────────
function StepsWidget({ steps = 2810, goal = 10000 }) {
  const disp = useCountUp(steps, 1300, 500);
  const pct = steps / goal;
  const fill = useRingFill(pct, 500, 1300);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase' }}>STEPS</div>
      <div style={{ fontFamily: FM, fontSize: 15, color: C.ink, fontWeight: 600, letterSpacing: '-0.3px', lineHeight: 1 }}>
        {disp.toLocaleString()}
      </div>
      <div style={{ width: 64, height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${fill * 100}%`, borderRadius: 3,
          background: C.green, boxShadow: `0 0 5px ${C.green}`,
          transition: 'width 1.3s cubic-bezier(0.25,0.46,0.45,0.94)',
        }} />
      </div>
      <div style={{ fontFamily: FM, fontSize: 8, color: C.faint }}>
        {Math.round(pct * 100)}% OF {goal >= 1000 ? `${Math.round(goal / 1000)}K` : goal}
      </div>
    </div>
  );
}

// ─── SECTION HEAD ─────────────────────────────────────────────────
function fmtSleep(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h ${String(r).padStart(2, '0')}m`;
}

export function SyncCell({ label, value, format, color = C.green, dur = 900 }) {
  // useCountUp internally rounds — scale by 1000 internally so one-decimal
  // values (e.g. weight) interpolate smoothly through the count-up.
  const animated = useCountUp(Math.round((Number(value) || 0) * 1000), dur, 0);
  const current = animated / 1000;
  const display = format ? format(current) : Math.round(current).toLocaleString();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
      <span style={{ fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: '1.6px', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontFamily: FM, fontSize: 13, color, fontWeight: 600, letterSpacing: '-0.2px', whiteSpace: 'nowrap' }}>{display}</span>
    </div>
  );
}

function SyncFlashHUD({ result, dismissing }) {
  if (!result) return null;
  const ok = result.ok;
  const accent = ok ? C.green : '#ff5252';
  const fmtInt   = (n) => Math.round(n).toLocaleString();
  const fmtKcal  = (n) => `${Math.round(n).toLocaleString()} kcal`;
  const fmtWeight = (n) => `${n.toFixed(1)} kg`;
  const fmtSleepCount = (n) => fmtSleep(Math.round(n));
  const cells = ok ? [
    typeof result.steps === 'number' && result.steps > 0
      ? { key: 'steps',  label: 'STEPS',  value: result.steps,        format: fmtInt } : null,
    typeof result.activeBurn === 'number' && result.activeBurn > 0
      ? { key: 'active', label: 'ACTIVE', value: result.activeBurn,   format: fmtKcal } : null,
    typeof result.basalBurn === 'number' && result.basalBurn > 0
      ? { key: 'basal',  label: 'BASAL',  value: result.basalBurn,    format: fmtKcal } : null,
    typeof result.sleepMinutes === 'number' && result.sleepMinutes > 0
      ? { key: 'sleep',  label: 'SLEEP',  value: result.sleepMinutes, format: fmtSleepCount } : null,
    typeof result.weightKg === 'number' && result.weightKg > 0
      ? { key: 'weight', label: 'WEIGHT', value: result.weightKg,     format: fmtWeight } : null,
    typeof result.bodyFatPct === 'number' && result.bodyFatPct > 0
      ? { key: 'bodyfat', label: 'BODY FAT', value: result.bodyFatPct, format: (n) => `${n.toFixed(1)}%` } : null,
  ].filter(Boolean) : [];
  const sr = ok
    ? `Synced from Apple Health: ${cells.map(c => `${c.label.toLowerCase()} ${c.format(c.value)}`).join(', ')}`
    : 'Apple Health sync unavailable';
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 11,
        padding: 'calc(10px + env(safe-area-inset-top)) 14px 10px',
        background: ok
          ? 'linear-gradient(180deg, rgba(0,230,118,0.10), rgba(7,11,9,0.85))'
          : 'linear-gradient(180deg, rgba(255,82,82,0.12), rgba(7,11,9,0.85))',
        backdropFilter: 'blur(14px) saturate(140%)',
        WebkitBackdropFilter: 'blur(14px) saturate(140%)',
        borderBottom: `1px solid ${ok ? 'rgba(0,230,118,0.35)' : 'rgba(255,82,82,0.4)'}`,
        boxShadow: ok ? '0 6px 24px rgba(0,230,118,0.12)' : '0 6px 24px rgba(255,82,82,0.12)',
        animation: `${dismissing ? 'hayah-hud-out' : 'hayah-hud-drop'} ${dismissing ? '220ms' : '300ms'} cubic-bezier(0.22,1,0.36,1) both`,
        pointerEvents: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 18, height: 18, borderRadius: '50%',
          background: ok ? 'rgba(0,230,118,0.18)' : 'rgba(255,82,82,0.18)',
          border: `1px solid ${accent}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: accent, fontFamily: FM, fontSize: 11, fontWeight: 700,
          boxShadow: `0 0 10px ${ok ? 'rgba(0,230,118,0.45)' : 'rgba(255,82,82,0.45)'}`,
          flexShrink: 0,
        }}>{ok ? '✓' : '!'}</div>
        <span style={{ fontFamily: FM, fontSize: 9, letterSpacing: '2.2px', color: accent, textTransform: 'uppercase', flexShrink: 0 }}>
          {ok ? 'APPLE HEALTH · SYNCED' : 'HEALTH SYNC UNAVAILABLE'}
        </span>
        <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${accent}55, transparent)` }} />
      </div>
      {ok && cells.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 22px', marginTop: 8, paddingLeft: 28 }}>
          {cells.map(c => <SyncCell key={c.key} label={c.label} value={c.value} format={c.format} color={accent} />)}
        </div>
      )}
      <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
        {sr}
      </span>
    </div>
  );
}

export function SecHead({ n, title, color = C.green, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
      <div style={{ width: 14, height: 1, background: color, opacity: 0.5 }} />
      <span style={{
        fontFamily: FM, fontSize: 9.5, color, letterSpacing: '1.8px',
        textTransform: 'uppercase', opacity: 0.8,
      }}>{n} · {title}</span>
      <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
      {right && <span style={{ fontFamily: FM, fontSize: 9, color: C.faint }}>{right}</span>}
    </div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────
export default function HayahToday({
  lastSyncLabel,
  onOpenMetric,
  onOpenLog,
}) {
  const open = (key) => () => { if (onOpenMetric) { try { hapticTap(); } catch {} onOpenMetric(key); } };
  const { prefs } = useHayahPrefs();
  const { data, bumpTick } = useHayahTodayData(prefs);
  const {
    intake, intakeGoal, burned, activeBurn, remaining, netDeficit,
    protein, carbs, fat, steps, stepsGoal, weightKg, bodyFatPct, sleep, fasting,
    streakDays, mounjaro, ketone,
  } = data;
  const [fastingOverlay, setFastingOverlay] = useState(false);

  // ── Background readiness auto-generator ───────────────────────
  // The Readiness UI now lives on the AI tab (HayahAIInsights). We keep
  // the silent auto-fire here because it's tied to the pull-to-refresh /
  // visibility Apple Health sync that this screen owns. Writes straight
  // to localStorage["hayahReadinessToday"]; the AI tab reads it on mount.
  const readinessGeneratingRef = useRef(false);
  const maybeAutoReadiness = useCallback(async (sleepMinutes) => {
    if (readinessGeneratingRef.current) return;
    if (loadReadinessCache()) return;
    if (!sleepMinutes || sleepMinutes <= 0) return;
    if (!getGeminiKey()) return;
    readinessGeneratingRef.current = true;
    try {
      const allData = readAllData();
      await generateReadiness(allData, { steveToday: false });
    } catch {} finally {
      readinessGeneratingRef.current = false;
    }
  }, []);

  // ── Ketone tile state ─────────────────────────────────────────
  const [ketoneLogging, setKetoneLogging] = useState(false);   // camera in flight
  const [ketoneManual, setKetoneManual]   = useState(false);   // manual entry mode
  const [ketoneValue,  setKetoneValue]    = useState("");      // manual input value
  const [ketoneConfirm, setKetoneConfirm] = useState(null);    // { rawValue, rawUnit }
  const [ketoneToast,  setKetoneToast]    = useState(null);    // success message

  const showKetoneToast = useCallback((msg) => {
    setKetoneToast(msg);
    setTimeout(() => setKetoneToast(null), 3000);
  }, []);

  const handleKetoneCamera = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setKetoneManual(true);
      return;
    }
    setKetoneLogging(true);
    try {
      const photo = await CapacitorCamera.getPhoto({
        quality: 80, allowEditing: false, resultType: 'dataUrl', source: 'PROMPT',
      });
      const imageUrl = photo.dataUrl || photo.base64String;
      if (!imageUrl) throw new Error("No image returned");
      const result = await analyseKetonePhoto(imageUrl);
      if (result.committed) {
        bumpTick();
        showKetoneToast(`Logged: ${result.entry.mmol} mmol/L ✓`);
        try { hapticHayahSuccess(); } catch {}
      } else if (result.needsConfirm) {
        setKetoneConfirm({ rawValue: result.rawValue, rawUnit: result.rawUnit });
      }
    } catch (e) {
      if (!/cancel/i.test(e.message || "")) showKetoneToast("Could not read meter — try manual entry");
      setKetoneManual(true);
    } finally {
      setKetoneLogging(false);
    }
  }, [bumpTick, showKetoneToast]);

  const handleKetoneSaveManual = useCallback(() => {
    const v = parseFloat(ketoneValue);
    if (!Number.isFinite(v) || v < 0) return;
    addKetoneReading({ mmol: v, unit: "mmol/L", source: "manual" });
    bumpTick();
    setKetoneManual(false);
    setKetoneValue("");
    showKetoneToast(`Logged: ${v} mmol/L ✓`);
    try { hapticHayahSuccess(); } catch {}
  }, [ketoneValue, bumpTick, showKetoneToast]);

  const handleKetoneConfirmSave = useCallback(() => {
    if (!ketoneConfirm) return;
    const v = parseFloat(ketoneConfirm.rawValue);
    if (Number.isFinite(v) && v >= 0) {
      addKetoneReading({ mmol: v, unit: ketoneConfirm.rawUnit || "mmol/L", source: "photo" });
      bumpTick();
      showKetoneToast(`Logged: ${v} mmol/L ✓`);
      try { hapticHayahSuccess(); } catch {}
    }
    setKetoneConfirm(null);
  }, [ketoneConfirm, bumpTick, showKetoneToast]);

  // (AI Summary + computed Today insights moved to HayahAIInsights on the AI tab.)

  const fastingThresholds = {
    ok:   prefs.fasting.okHours,
    good: prefs.fasting.goodHours,
    goal: prefs.fasting.goalHours,
  };
  const handleFastingEdit = useCallback((date, patch) => {
    patchFastingOverride(date, patch);
    bumpTick();
  }, [bumpTick]);
  const handleFastingReset = useCallback((date) => {
    patchFastingOverride(date, null);
    bumpTick();
  }, [bumpTick]);
  const remComputed = remaining;
  const syncLabel = lastSyncLabel ?? (data.hasData ? "LIVE" : "NO DATA");

  // ── Pull-to-refresh + auto-sync state ───────────────────────────
  const [pullY, setPullY] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [hudDismissing, setHudDismissing] = useState(false);
  const hudTimersRef = useRef({ dismiss: null, clear: null });
  const pullRef = useRef({ startY: 0, active: false });
  const scrollRef = useRef(null);
  const THRESHOLD = 64;

  useEffect(() => () => {
    if (hudTimersRef.current.dismiss) clearTimeout(hudTimersRef.current.dismiss);
    if (hudTimersRef.current.clear)   clearTimeout(hudTimersRef.current.clear);
  }, []);

  const flashSyncResult = useCallback((result) => {
    if (hudTimersRef.current.dismiss) clearTimeout(hudTimersRef.current.dismiss);
    if (hudTimersRef.current.clear)   clearTimeout(hudTimersRef.current.clear);
    setHudDismissing(false);
    setSyncResult(result);
    hudTimersRef.current.dismiss = setTimeout(() => {
      setHudDismissing(true);
      hudTimersRef.current.clear = setTimeout(() => {
        setSyncResult(null);
        setHudDismissing(false);
      }, 240);
    }, 3000);
  }, []);

  // Auto-sync on mount + visibilitychange when prefs.sync.appleHealthAutoOnFocus.
  // Fires readiness generation after a successful sync if no cache yet for today.
  const autoSyncedRef = useRef(false);
  useEffect(() => {
    if (!prefs?.sync?.appleHealthAutoOnFocus) return;
    const run = async () => {
      if (autoSyncedRef.current) return;
      autoSyncedRef.current = true;
      setIsSyncing(true);
      const result = await pullHealthForToday(bumpTick);
      setIsSyncing(false);
      if (result?.ok) maybeAutoReadiness(result.sleepMinutes);
    };
    run();
    const onVis = () => {
      if (document.visibilityState === "visible") {
        autoSyncedRef.current = false;
        run();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (isSyncing) return;
    const el = scrollRef.current;
    if (!el || el.scrollTop > 0) return;
    pullRef.current = { startY: e.touches[0].clientY, active: true };
  }, [isSyncing]);

  const handleTouchMove = useCallback((e) => {
    if (!pullRef.current.active || isSyncing) return;
    const dy = e.touches[0].clientY - pullRef.current.startY;
    if (dy <= 0) { pullRef.current.active = false; setPullY(0); return; }
    // Rubber-band: full drag up to THRESHOLD, then taper
    setPullY(Math.min(dy * 0.5, THRESHOLD + 16));
  }, [isSyncing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pullRef.current.active) return;
    pullRef.current.active = false;
    if (pullY >= THRESHOLD) {
      setPullY(THRESHOLD); // snap to indicator height while syncing
      setIsSyncing(true);
      const result = await pullHealthForToday(bumpTick);
      setIsSyncing(false);
      flashSyncResult(result || { ok: false });
      if (result?.ok) maybeAutoReadiness(result.sleepMinutes);
    }
    setPullY(0);
  }, [pullY, bumpTick, flashSyncResult, maybeAutoReadiness]);

  return (
    <div
      className={prefs.motion.reduce ? "hayah-reduce-motion" : undefined}
      style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        .hayah-reduce-motion *,
        .hayah-reduce-motion *::before,
        .hayah-reduce-motion *::after {
          animation: none !important;
          transition: none !important;
        }
        @keyframes hayah-blink-dot{0%,100%{opacity:1}50%{opacity:0.2}}
        @keyframes hayah-ptr-sweep{
          0%{transform:translateX(-100%)}
          100%{transform:translateX(100%)}
        }
        @keyframes hayah-ptr-pulse{
          0%,100%{opacity:0.55}
          50%{opacity:1}
        }
        @keyframes hayah-hud-drop{
          from{transform:translateY(-100%);opacity:0}
          to{transform:translateY(0);opacity:1}
        }
        @keyframes hayah-hud-out{
          from{transform:translateY(0);opacity:1}
          to{transform:translateY(-110%);opacity:0}
        }
        .hayah-today-scroll::-webkit-scrollbar{display:none}
        .hayah-today-scroll{scrollbar-width:none}
      `}</style>

      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: 'radial-gradient(ellipse at 50% -5%, rgba(0,230,118,0.08), transparent 50%), #070b09',
      }} />
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,230,118,0.02) 0 1px, transparent 1px 4px)',
      }} />

      {/* Pull-to-refresh indicator — HUD scanline */}
      {(pullY > 4 || isSyncing) && (() => {
        const armed = isSyncing || pullY >= THRESHOLD;
        const progress = Math.min(pullY / THRESHOLD, 1);
        const caption = isSyncing
          ? 'SYNCING APPLE HEALTH'
          : armed
            ? 'RELEASE TO SYNC'
            : 'PULL TO SYNC';
        return (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 8, height: isSyncing ? THRESHOLD : pullY,
            overflow: 'hidden', pointerEvents: 'none',
            paddingTop: 'env(safe-area-inset-top)',
            transition: pullY === 0 && !isSyncing ? 'height 0.25s ease' : 'none',
          }}>
            {/* Scanline / sweep bar */}
            <div style={{
              position: 'relative',
              width: armed ? 132 : `${Math.max(40, 40 + progress * 92)}px`,
              height: 1.5,
              background: armed ? C.green : 'rgba(0,230,118,0.35)',
              boxShadow: armed
                ? '0 0 14px rgba(0,230,118,0.85), 0 0 4px rgba(0,230,118,0.9)'
                : '0 0 6px rgba(0,230,118,0.35)',
              opacity: Math.max(progress, isSyncing ? 1 : 0),
              overflow: 'hidden',
              transition: 'width 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
            }}>
              {isSyncing && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)',
                  animation: 'hayah-ptr-sweep 1.1s ease-in-out infinite',
                }} />
              )}
            </div>
            {/* Caption */}
            <span style={{
              fontFamily: FM, fontSize: 8.5, letterSpacing: '2.6px',
              color: armed ? C.green : 'rgba(0,230,118,0.55)',
              textTransform: 'uppercase',
              opacity: Math.max(progress, isSyncing ? 1 : 0),
              animation: isSyncing ? 'hayah-ptr-pulse 1.4s ease-in-out infinite' : 'none',
              textShadow: armed ? '0 0 10px rgba(0,230,118,0.55)' : 'none',
              transition: 'color 0.18s ease',
            }}>{caption}</span>
          </div>
        );
      })()}

      {/* Post-sync HUD readout */}
      <SyncFlashHUD result={syncResult} dismissing={hudDismissing} />

      <div
        ref={scrollRef}
        className="hayah-today-scroll"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{
          position: 'relative', zIndex: 2, height: '100%', overflowY: 'auto',
          fontFamily: FS, color: C.ink,
          transform: pullY > 0 ? `translateY(${pullY}px)` : 'none',
          transition: pullY === 0 ? 'transform 0.25s ease' : 'none',
        }}>
        {/* status row */}
        <div style={{ padding: '10px 16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 7, height: 7, borderRadius: 7, background: C.green,
                boxShadow: `0 0 8px ${C.green}`, animation: 'hayah-blink-dot 2s ease-in-out infinite',
              }} />
              <span style={{
                fontFamily: FM, fontSize: 13, color: C.green, letterSpacing: '1.6px',
                textTransform: 'uppercase', fontWeight: 600,
              }}>HAYAH</span>
              <span style={{
                fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1px',
                textTransform: 'uppercase',
              }}>LIVE</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11 }}>🔥</span>
              <span style={{ fontFamily: FM, fontSize: 10, color: C.ink }}>{streakDays}-day</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} style={{
                    width: 6, height: 6, borderRadius: 6,
                    background: i < streakDays ? C.amber : 'rgba(255,255,255,0.1)',
                    boxShadow: i < streakDays ? `0 0 5px ${C.amber}` : 'none',
                  }} />
                ))}
              </div>
            </div>
          </div>
          {/* LIVE FASTING PILL — at-a-glance state without opening the card.
              Eating mode: "🍽 4h 12m · since LUNCH". Fasting mode (in
              progress): "🔥 14h 23m · KETOSIS". Tap to scroll to the
              full fasting card below. */}
          {prefs.fasting.enabled && prefs.fasting.visibleOnToday && (() => {
            const cm = fasting?.currentMeal;
            const eating = cm?.mode === "eating" && Number.isFinite(cm?.hoursSinceLastMeal) && cm.hoursSinceLastMeal < 24;
            const liveH = eating ? cm.hoursSinceLastMeal : (fasting?.inProgress ? fasting?.hours : null);
            if (liveH == null || !Number.isFinite(liveH)) return null;
            const stage = (cm?.stage) || (FASTING_STAGES.find(s => liveH >= s.h0 && liveH < s.h1) || FASTING_STAGES[FASTING_STAGES.length - 1]);
            const stageColors = {
              digesting: C.cream, glycogen: C.amber, ketosis: C.green,
              autophagy: C.green, deep: '#7AE7C7',
            };
            const sc = stageColors[stage.slug] || C.amber;
            const label = eating
              ? `since ${(cm.lastMealName || cm.lastMealSlot || 'meal').toString().toUpperCase().slice(0, 14)}`
              : stage.label.toUpperCase();
            const icon = eating ? '🍽' : '🔥';
            return (
              <button
                onClick={() => {
                  try { hapticTap(); } catch {}
                  const el = document.getElementById('hayah-fasting-card');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                style={{
                  width: '100%', marginBottom: 8, padding: '7px 10px',
                  background: `linear-gradient(90deg, ${sc}18, transparent)`,
                  border: `1px solid ${sc}33`, borderRadius: 8,
                  display: 'flex', alignItems: 'center', gap: 8,
                  cursor: 'pointer', fontFamily: FM,
                  WebkitTapHighlightColor: 'transparent',
                }}>
                <span style={{ fontSize: 12 }}>{icon}</span>
                <span style={{
                  fontSize: 11, color: C.ink, fontWeight: 700, letterSpacing: '0.5px',
                }}>{Math.floor(liveH)}h {String(Math.round((liveH % 1) * 60)).padStart(2, '0')}m</span>
                <span style={{
                  fontSize: 8, color: sc, letterSpacing: '1.2px',
                  textTransform: 'uppercase', fontWeight: 600,
                }}>· {label}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 8, color: C.faint, letterSpacing: '1px' }}>OPEN ↓</span>
              </button>
            );
          })()}
        </div>

        {/* ═══ 01 · ENERGY (hero dashboard) ═══════════════════════════════
            Single visual block: orbit cluster (intake/burned/left) on top,
            then weight/steps/deficit strip, then macro meters.
            One section header — internal dividers separate, not new headers. */}
        <div style={{ padding: '7px 14px 4px' }}>
          <SecHead n="01" title="ENERGY" color={C.green} right={syncLabel} />
          <div style={{
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 18,
            padding: '4px 0 10px',
          }}>
            <OrbitCluster
              intake={intake} intakeGoal={intakeGoal} burned={burned} remaining={remComputed}
              onIntake={onOpenLog ? () => { try { hapticTap(); } catch {} onOpenLog(); } : undefined}
              onBurned={onOpenMetric ? open('burned') : undefined}
              onRemaining={onOpenMetric ? open('remaining') : undefined}
            />

            {/* inline weight · steps · net deficit strip */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              margin: '2px 14px 8px',
              padding: '8px 4px',
              borderTop: '1px solid rgba(255,255,255,0.06)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div>
                <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase' }}>WEIGHT</div>
                <div style={{ fontFamily: FM, fontSize: 15, color: C.ink, fontWeight: 600, marginTop: 2 }}>
                  {weightKg > 0 ? weightKg.toFixed(1) : '—'}
                  <span style={{ fontSize: 8, color: C.dim }}>{weightKg > 0 ? 'kg' : ''}</span>
                </div>
                <div style={{ fontFamily: FM, fontSize: 8, color: bodyFatPct > 0 ? C.amber : C.faint, marginTop: 1 }}>
                  {bodyFatPct > 0 ? `${bodyFatPct.toFixed(1)}% BODY FAT` : (weightKg > 0 ? '7D AVG · STEADY' : 'NO DATA')}
                </div>
              </div>
              <Tap onTap={onOpenMetric ? open('activity') : undefined} label="Open activity detail">
                <StepsWidget steps={steps} goal={stepsGoal} />
              </Tap>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase' }}>NET</div>
                <div style={{ fontFamily: FM, fontSize: 15, color: netDeficit < 0 ? C.green : netDeficit > 0 ? C.coral : C.dim, fontWeight: 600, marginTop: 2 }}>
                  {netDeficit > 0 ? '+' : netDeficit < 0 ? '−' : ''}{Math.abs(netDeficit)}<span style={{ fontSize: 8, color: C.dim }}>kcal</span>
                </div>
                <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, marginTop: 1 }}>{netDeficit < 0 ? 'DEFICIT' : netDeficit > 0 ? 'SURPLUS' : 'EVEN'}</div>
              </div>
            </div>

            {/* inline macros — no separate section header */}
            <div style={{ display: 'flex', justifyContent: 'space-around', padding: '4px 8px 0' }}>
              <Tap onTap={onOpenMetric ? open('protein') : undefined} label="Open protein detail">
                <LedMeter label="Protein" value={protein.value} max={protein.max} color={C.cream} seg={12} delay={500} />
              </Tap>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', margin: '4px 0' }} />
              <Tap onTap={onOpenMetric ? open('carbs') : undefined} label="Open carbs detail">
                <LedMeter label="Carbs" value={carbs.value} max={carbs.max} color={C.amber} seg={12} delay={600} />
              </Tap>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', margin: '4px 0' }} />
              <Tap onTap={onOpenMetric ? open('fat') : undefined} label="Open fat detail">
                <LedMeter label="Fat" value={fat.value} max={fat.max} color={C.coral} seg={12} delay={700} />
              </Tap>
            </div>
          </div>
        </div>

        {/* ═══ 02 · FASTING & METABOLIC ═════════════════════════════════════
            One chapter for everything eating-protocol: the live fasting card
            (window, milestones, timeline) sits above a compact metabolic
            readout strip — fast hours · ketones · Tirzepatide cycle day.
            All three describe how the user is eating today; biometric stuff
            (sleep / activity) moves to Recovery below. */}
        {(prefs.fasting.enabled || mounjaro || ketone) && (
          <div style={{ padding: '14px 14px 0' }}>
            <SecHead n="02" title="FASTING & METABOLIC" color={C.amber} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {prefs.fasting.enabled && prefs.fasting.visibleOnToday && (
                <div id="hayah-fasting-card">
                  <Tap onTap={() => setFastingOverlay(true)} label="Open fasting detail">
                    <FastingCard {...fasting} thresholds={fastingThresholds} timeFmt={prefs.units.time} />
                  </Tap>
                </div>
              )}

              {/* Compact 3-up strip: fast hours · ketones · Tirzepatide */}
              <div style={{
                display: 'flex', gap: 0,
                background: 'rgba(255,255,255,0.025)', borderRadius: 14, padding: '12px 14px',
                border: '1px solid rgba(255,255,255,0.06)',
              }}>
                {prefs.fasting.enabled && (
                  <div style={{ flex: 1, borderRight: '1px solid rgba(255,255,255,0.07)', paddingRight: 12, marginRight: 12 }}>
                    <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase', marginBottom: 5 }}>FAST</div>
                    {fasting.inProgress ? (
                      <>
                        <div style={{ fontFamily: FM, fontSize: 17, color: C.amber, fontWeight: 700, lineHeight: 1 }}>
                          {Math.floor((fasting.durationMins || 0) / 60)}h{String(Math.round((fasting.durationMins || 0) % 60)).padStart(2, '0')}m
                        </div>
                        <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, marginTop: 3 }}>IN PROGRESS</div>
                      </>
                    ) : (fasting.completedHours > 0) ? (
                      <>
                        <div style={{ fontFamily: FM, fontSize: 17, color: C.green, fontWeight: 700, lineHeight: 1 }}>
                          {(fasting.completedHours || 0).toFixed(1)}h
                        </div>
                        <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, marginTop: 3 }}>COMPLETE</div>
                      </>
                    ) : (
                      <div style={{ fontFamily: FM, fontSize: 13, color: C.faint, lineHeight: 1 }}>—</div>
                    )}
                  </div>
                )}
                <div style={{ flex: 1, borderRight: mounjaro ? '1px solid rgba(255,255,255,0.07)' : 'none', paddingRight: mounjaro ? 12 : 0, marginRight: mounjaro ? 12 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                    <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase' }}>KETONES</div>
                    <button
                      onClick={handleKetoneCamera}
                      disabled={ketoneLogging}
                      title="Log ketone reading"
                      style={{ background: 'none', border: 'none', padding: '1px 2px', cursor: 'pointer', color: C.faint, fontSize: 12, lineHeight: 1 }}>
                      {ketoneLogging ? '…' : '📷'}
                    </button>
                  </div>
                  {ketone ? (
                    <>
                      <div style={{ fontFamily: FM, fontSize: 17, color: C.teal, fontWeight: 700, lineHeight: 1 }}>{(ketone.mmol || 0).toFixed(1)}</div>
                      <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, marginTop: 3 }}>mmol/L</div>
                    </>
                  ) : (
                    <button
                      onClick={handleKetoneCamera}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FM, fontSize: 11, color: C.faint, textAlign: 'left', lineHeight: 1 }}>
                      Log ketones →
                    </button>
                  )}
                </div>
                {mounjaro && (
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase', marginBottom: 5 }}>TIRZEPATIDE</div>
                    <div style={{ fontFamily: FM, fontSize: 17, color: C.coral, fontWeight: 700, lineHeight: 1 }}>Day {mounjaro.dayOfCycle}</div>
                    <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, marginTop: 3 }}>OF 7</div>
                  </div>
                )}
              </div>

              {ketoneManual && (
                <div style={{ background: 'rgba(95,209,185,0.06)', borderRadius: 12, padding: '12px 14px', border: '1px solid rgba(95,209,185,0.2)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="number" placeholder="e.g. 1.4" value={ketoneValue}
                    onChange={e => setKetoneValue(e.target.value)}
                    step="0.1" min="0"
                    style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: C.ink, borderRadius: 8, padding: '8px 10px', fontFamily: FM, fontSize: 14 }}
                  />
                  <span style={{ fontFamily: FM, fontSize: 10, color: C.faint }}>mmol/L</span>
                  <button onClick={handleKetoneSaveManual} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(95,209,185,0.35)', background: 'rgba(95,209,185,0.12)', color: C.teal, fontFamily: FM, fontSize: 10, cursor: 'pointer' }}>Save</button>
                  <button onClick={() => { setKetoneManual(false); setKetoneValue(""); }} style={{ padding: '8px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: C.faint, fontFamily: FM, fontSize: 10, cursor: 'pointer' }}>✕</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ 03 · RECOVERY — sleep + activity ════════════════════════════ */}
        <div style={{ padding: '14px 14px 0' }}>
          <SecHead n="03" title="RECOVERY" color={C.deepBlue} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Tap onTap={onOpenMetric ? open('sleep') : undefined} label="Open sleep detail">
              <SleepCard {...sleep} />
            </Tap>
            <Tap onTap={onOpenMetric ? open('activity') : undefined} label="Open activity detail">
              <BurnCard total={burned} active={activeBurn} color={C.green} />
            </Tap>
          </div>
        </div>

        {/* Bottom padding so the last card clears the tab bar */}
        <div style={{ height: 28 }} />
      </div>

      {/* Ketone confirm overlay — low-confidence OCR */}
      {ketoneConfirm && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'rgba(18,24,18,0.98)', borderRadius: 18, padding: 24, maxWidth: 320, width: '100%', border: '1px solid rgba(95,209,185,0.3)' }}>
            <div style={{ fontFamily: FM, fontSize: 11, color: C.teal, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 12 }}>Confirm Ketone Reading</div>
            <div style={{ fontFamily: FM, fontSize: 32, color: C.ink, fontWeight: 700, marginBottom: 4 }}>{ketoneConfirm.rawValue}</div>
            <div style={{ fontFamily: FM, fontSize: 11, color: C.faint, marginBottom: 20 }}>{ketoneConfirm.rawUnit || 'mmol/L'} · Reading unclear — please confirm</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleKetoneConfirmSave} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid rgba(95,209,185,0.4)', background: 'rgba(95,209,185,0.14)', color: C.teal, fontFamily: FM, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Confirm</button>
              <button onClick={() => { setKetoneConfirm(null); setKetoneManual(true); }} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'transparent', color: C.faint, fontFamily: FM, fontSize: 12, cursor: 'pointer' }}>Enter Manually</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Ketone success toast */}
      {ketoneToast && createPortal(
        <div style={{ position: 'fixed', bottom: 96, left: '50%', transform: 'translateX(-50%)', zIndex: 400, background: 'rgba(95,209,185,0.18)', border: '1px solid rgba(95,209,185,0.4)', borderRadius: 12, padding: '10px 18px', pointerEvents: 'none', fontFamily: FM, fontSize: 11, color: C.teal, letterSpacing: '0.8px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', whiteSpace: 'nowrap' }}>
          {ketoneToast}
        </div>,
        document.body
      )}

      <FastingDetailOverlay
        open={fastingOverlay}
        onClose={() => setFastingOverlay(false)}
        fasting={fasting}
        thresholds={fastingThresholds}
        timeFmt={prefs.units.time}
        showBenefitsTimeline={prefs.fasting.showBenefitsTimeline}
        onEditOverride={handleFastingEdit}
        onResetOverride={handleFastingReset}
      />
    </div>
  );
}
